import React from 'react';
import { motion } from 'framer-motion';
import { useBehandelingenApproach } from '../hooks/useBehandelingenApproach';
import { getImageUrl } from '../utils/imageUrl';
import { getCurrentLocale } from '../services/api';

interface ApproachStep {
  number: number;
  title: string;
  description: string;
}

interface BehandelingenApproachData {
  title: string;
  steps: ApproachStep[];
  image?: {
    data?: {
      attributes?: {
        url?: string;
        alternativeText?: string;
      };
    };
  };
}

function BehandelingenApproachSection() {
  const { data, loading, error } = useBehandelingenApproach();
  const locale = getCurrentLocale();
  
  // Debug logging
  console.log('🔍 BehandelingenApproachSection - data:', data);
  console.log('🔍 BehandelingenApproachSection - loading:', loading);
  console.log('🔍 BehandelingenApproachSection - error:', error);

  // Default data as fallback
  const defaultData: BehandelingenApproachData = {
    title: "Onze werkwijze in 3 stappen",
    steps: [
      {
        number: 1,
        title: "Analyse",
        description: "Tijdens de intake doen wij een microscopisch onderzoek om te ontdekken waarom je haar uitvalt of dunner wordt."
      },
      {
        number: 2,
        title: "Persoonlijke behandeling",
        description: "Op basis van jouw analyse stellen we een unieke cocktail samen met actieve ingrediënten. Deze wordt met een fijne 'meso gun' in de huid ingebracht om haargroei te stimuleren."
      },
      {
        number: 3,
        title: "Opvolging & nazorg",
        description: "Na een aantal sessies checken we samen de voortgang. Je krijgt ook productadvies en nazorg die je thuis kunt toepassen voor blijvend resultaat."
      }
    ]
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Extract data from Strapi structure or use fallback (support both shapes)
  const flatData = (data as any) || null;
  const attrData = (data as any)?.data?.attributes;
  const strapiData = flatData && typeof flatData === 'object' && 'steps' in flatData
    ? flatData
    : attrData;
  const sectionData = strapiData || defaultData;

  // Handle image URL - support both flat and data.attributes image shapes
  const imageUrl = strapiData?.image?.data?.attributes?.url
    ? getImageUrl(strapiData.image.data.attributes.url)
    : strapiData?.image?.url
    ? getImageUrl(strapiData.image.url)
    : "/205d904e1a2de7595526a2f1287fb9326166fa06.png"; // Fallback image from public folder

  if (loading) {
    return (
      <section className="behandelingen-approach-section section-padding">
        <div className="container">
          <div className="approach-content">
            <div className="approach-text">
              <h2 className="approach-title werkwijze-title">{defaultData.title}</h2>
              <div className="steps-container">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="step-item">
                    <div className="loading-placeholder">
                      <div className="loading-title"></div>
                      <div className="loading-text"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="approach-image">
              <img src="/205d904e1a2de7595526a2f1287fb9326166fa06.png" alt="Loading..." className="approach-img" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="behandelingen-approach-section section-padding">
        <div className="container">
          <div className="approach-content">
            <div className="approach-text">
              <h2 className="approach-title werkwijze-title">{defaultData.title}</h2>
              <div className="steps-container">
                {defaultData.steps.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">{step.number}</div>
                    <div className="step-content">
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-description">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="approach-image">
              <img src="/205d904e1a2de7595526a2f1287fb9326166fa06.png" alt="Behandelingen approach" className="approach-img" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      className="behandelingen-approach-section section-padding"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="container">
        <div className="approach-content">
          <motion.div className="approach-text" variants={itemVariants}>
            <h2 className="approach-title werkwijze-title">{sectionData.title}</h2>
            <div className="steps-container">
              {sectionData.steps.map((step, index) => (
                <motion.div 
                  key={index} 
                  className="step-item"
                  variants={itemVariants}
                >
                  <div className="step-number">{step.number}</div>
                  <div className="step-content">
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div className="approach-image" variants={itemVariants}>
            <img 
              src={imageUrl} 
              alt={strapiData?.image?.data?.attributes?.alternativeText || "Behandelingen approach"} 
              className="approach-img"
            />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

export default BehandelingenApproachSection;